import React, { useEffect, useState } from 'react';
import './bottom-nav.css';
import { getSupabase } from '../lib/supabase.js';

// Mobile-only bottom tab bar for the investment module.
//
// Rendered by App.jsx for the investor 'ready' state and the admin
// "acting as investor" state. Hidden on desktop entirely via CSS
// (display:none until the @media (max-width:480px) breakpoint), so it has
// zero effect on the desktop experience.
//
// Re-prioritized 2026-06-08 around the investor's daily jobs (record a
// movement, see what's next, see balances) instead of monthly tasks:
//   Home · Balances · ➕ Log (center) · Activity · More
// Catalog / Settlements / Connectivity / Add sub-account / Edit allocations
// all moved into the More hub (they're monthly/rare). The center Log button
// is an ACTION, not a route — it dispatches `finops:open-log`, which App
// listens for and opens the LogMovementModal (so logging is one tap from
// any screen).

// Shared with DesktopNav.jsx (the ≥481px top strip): same tabs, same icons,
// same active-tab mapping, one Log action. Keep the two navs in lockstep by
// editing these exports, not either component.
export const ICONS = {
  home: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 10.5 12 3l9 7.5M5 9.5V20h14V9.5"/></svg>
  ),
  balances: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="2.5" y="6" width="19" height="13" rx="2.5"/><path d="M2.5 10.5h19M6 15h4"/></svg>
  ),
  activity: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 7h16M4 12h16M4 17h10"/></svg>
  ),
  more: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="5" cy="12" r="1.7"/><circle cx="12" cy="12" r="1.7"/><circle cx="19" cy="12" r="1.7"/></svg>
  ),
  admin: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
  ),
  plus: (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
  ),
};

// Route tabs (left + right of the center action) → the screen each navigates to.
export const TABS = [
  { key: 'home',     label: 'Home',     screen: 'home' },
  { key: 'balances', label: 'Balances', screen: 'balances' },
  { key: 'activity', label: 'Activity', screen: 'activity' },
  { key: 'more',     label: 'More',     screen: 'more' },
];

// T-042: appended to TABS only while an admin is acting as an investor —
// the household's admin controls (terms, loops, family, settlements) live
// on this tab of the investor view. Both navs take the same `adminTab`
// prop, so the two stay in lockstep.
export const ADMIN_TAB = { key: 'admin', label: 'Admin', screen: 'admin-settings' };

// Which bottom-nav tab should appear active for a given current screen.
// Sub-screens map back to the tab they were launched from. The monthly/rare
// screens that now live under More keep the More tab lit.
// (T-043: 'edit-allocations' retired with the allocations concept.)
export const PARENT_TAB = {
  home: 'home',
  balances: 'balances',
  activity: 'activity',
  more: 'more',
  'admin-settings': 'admin',
  // everything reached through More
  'step-detail': 'more',
  settlements: 'more',
  'settle-now': 'more',
  'add-subaccount': 'more',
  'email-sources': 'more',
  'agent-health': 'more',
};

// Form-style screens carry their own sticky action footer (Save / Next /
// Settle). The bottom nav would collide with that footer, so we hide it on
// these screens — matching the approved mockup, where forms are full-screen.
const FORM_SCREENS = new Set(['settle-now', 'add-subaccount']);

// Open the log-movement modal from anywhere. App.jsx listens for this.
export function openLog() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('finops:open-log'));
  }
}

// T-009 Build 1 (decision 4, 2026-08-08): pending TK-C draws show a count
// badge on the Home tab in BOTH navs. The count comes from
// pending_draws_for_member — the same RLS-scoped view any future external
// notifier will consume — so an investor counts their household and the
// admin counts everyone. Refreshes on the app's data-changed event plus a
// slow interval (the 5-minute fetch cadence makes anything faster noise).
export function usePendingDrawCount() {
  const [n, setN] = useState(0);
  useEffect(() => {
    const sb = getSupabase();
    if (!sb || typeof window === 'undefined') return undefined;
    let dead = false;
    const refresh = () => {
      sb.from('pending_draws_for_member')
        .select('movement_id', { count: 'exact', head: true })
        .then(({ count, error }) => { if (!dead && !error) setN(count || 0); }, () => {});
    };
    refresh();
    const onChange = () => refresh();
    window.addEventListener('finops:enrollment-changed', onChange);
    const t = setInterval(refresh, 120000);
    return () => {
      dead = true;
      window.removeEventListener('finops:enrollment-changed', onChange);
      clearInterval(t);
    };
  }, []);
  return n;
}

// Small amber count pill. BottomNav positions it over the icon (absolute);
// DesktopNav renders it inline after the label — pass inline for that.
export function DrawBadge({ count, inline = false }) {
  if (!count) return null;
  const base = {
    background: '#f59e0b', color: '#fff', borderRadius: 999,
    fontSize: 9.5, fontWeight: 800, lineHeight: '13px',
    minWidth: 13, height: 13, padding: '0 3px', textAlign: 'center',
  };
  const pos = inline
    ? { display: 'inline-block', marginLeft: 6, verticalAlign: 'middle' }
    : { position: 'absolute', top: 3, left: '50%', marginLeft: 7 };
  return (
    <span aria-label={`${count} pending card draw${count === 1 ? '' : 's'}`} style={{ ...base, ...pos }}>
      {count > 9 ? '9+' : count}
    </span>
  );
}

export default function BottomNav({ currentScreen, navigate, adminTab = false }) {
  const drawCount = usePendingDrawCount();
  if (FORM_SCREENS.has(currentScreen)) return null;
  const active = PARENT_TAB[currentScreen] || 'home';
  const tabs = adminTab ? [...TABS, ADMIN_TAB] : TABS;
  const [left, right] = [tabs.slice(0, 2), tabs.slice(2)];
  const renderTab = (t) => (
    <button
      key={t.key}
      type="button"
      className={'inv-nav-item' + (active === t.key ? ' active' : '')}
      aria-current={active === t.key ? 'page' : undefined}
      onClick={() => navigate(t.screen)}
      style={t.key === 'home' ? { position: 'relative' } : undefined}
    >
      <span className="inv-navic">{ICONS[t.key]}</span>
      <span className="inv-nav-lbl">{t.label}</span>
      {t.key === 'home' && <DrawBadge count={drawCount} />}
    </button>
  );
  return (
    <nav className="inv-botnav" role="navigation" aria-label="Investment sections">
      {left.map(renderTab)}
      <button
        type="button"
        className="inv-nav-item inv-nav-log"
        aria-label="Log a money movement"
        onClick={openLog}
      >
        <span className="inv-nav-fab">{ICONS.plus}</span>
        <span className="inv-nav-lbl inv-nav-log-lbl">Log</span>
      </button>
      {right.map(renderTab)}
    </nav>
  );
}
